import { enUS, es, fr, type Locale } from 'date-fns/locale';

/**
 * Map of supported UI locales to date-fns locale objects.
 * Used for localizing date formatting (e.g., "2 days ago").
 */
export const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
};

/**
 * Get date-fns locale from a locale string.
 * Handles BCP-47 tags like 'en-US' by extracting the base language.
 *
 * @param locale - The locale string (e.g., 'en', 'en-US', 'es-ES')
 * @returns The corresponding date-fns Locale, defaulting to enUS
 *
 * @example
 * ```ts
 * getDateFnsLocale('en')     // returns enUS
 * getDateFnsLocale('en-US')  // returns enUS
 * getDateFnsLocale('es-ES')  // returns es
 * getDateFnsLocale('de')     // returns enUS (fallback)
 * ```
 */
export function getDateFnsLocale(locale: string): Locale {
  // Try exact match first
  if (DATE_FNS_LOCALES[locale]) {
    return DATE_FNS_LOCALES[locale];
  }
  // Extract base language (e.g., 'en' from 'en-US')
  const baseLanguage = locale.split('-')[0];
  return DATE_FNS_LOCALES[baseLanguage] || enUS;
}
